import { useEffect } from 'react';
import {
  Editor,
  Element as SlateElement,
  Node as SlateNode,
  Text as SlateText,
  Transforms,
} from 'slate';
import { last } from 'lodash';

import { SLATE_TEXT_FORMATS } from 'src/components/RichSlate';
import * as MarkedEditorUtils from '../components/MarkedEditor';

export function getValueLength(value) {
  if (!value) {
    return 0;
  }

  const paragraphLength = (paragraph) =>
    paragraph.children.reduce((acc, leaf) => acc + leaf.text.length, 0);

  // Sum leaf length in each paragraph, start with "paragraph count - 1" - this is number of newlines
  return value.reduce((acc, paragraph) => acc + paragraphLength(paragraph), value.length - 1);
}

export const getEditorParagraphs = (editor) =>
  Editor.nodes(editor, {
    at: [],
    match: (node) => SlateElement.isElement(node) && node.type === SLATE_TEXT_FORMATS.PARAGRAPH,
  });

export const getNodeCharactersPoints = (editor, nodePath) => {
  const textNodes = Editor.nodes(editor, {
    at: Editor.range(editor, nodePath),
    match: (node) => SlateText.isText(node),
  });

  return [...textNodes]
    .map(([node, path]) => {
      const text = SlateNode.string(node);
      const chars = text.split('');
      return chars.map((char, index) => ({ char, path, offset: index }));
    })
    .flat();
};

export const getCharactersPointsBoundaries = (charactersPoints, start, end) => {
  if (!charactersPoints || !(start >= 0 && end >= 0)) {
    return null;
  }

  const startCharData = charactersPoints[start];
  let endCharData = charactersPoints[end];
  if (!endCharData) {
    const lastCharData = last(charactersPoints);
    endCharData = {
      path: lastCharData.path,
      offset: lastCharData.offset + 1,
    };
  }

  return {
    start: startCharData,
    end: endCharData,
  };
};

export const getParagraphsCharacterPoints = (editor, paragraphs) => {
  const result = [];

  // Get Slate paragraphs - element of type PARAGRAPH
  const slateParagraphsData = paragraphs.map(([, path]) => getNodeCharactersPoints(editor, path));

  // Each Slate paragraph can contain line-breaks, which we should split into real text paragraphs
  slateParagraphsData.forEach((paragraphData) => {
    let currentParagraph = [];

    paragraphData.forEach((charData) => {
      if (charData.char === '\n') {
        result.push(currentParagraph);
        currentParagraph = [];
      } else {
        currentParagraph.push(charData);
      }
    });

    result.push(currentParagraph);
  });

  return result;
};

export const applyEditorTextChanges = (editor, changes) => {
  const paragraphs = [...getEditorParagraphs(editor)];

  changes.forEach((change) => {
    // We need to get paragraphs character data for each change,
    // cause previous change might shift "text" paragraphs boundaries
    const paragraphsCharsPoints = getParagraphsCharacterPoints(editor, paragraphs);
    const paragraphCharsPoints = paragraphsCharsPoints[change.paragraph];
    if (!paragraphCharsPoints) {
      return;
    }

    const boundaries = getCharactersPointsBoundaries(
      paragraphCharsPoints,
      change.start,
      change.start + change.originText.length
    );
    if (boundaries) {
      Transforms.insertText(editor, change.newText, {
        at: {
          anchor: boundaries.start,
          focus: boundaries.end,
        },
      });
    }
  });
};

export const replaceSlateEditorValue = (editor, newEditorValue) => {
  if (!editor) {
    return;
  }

  // Remove selection, in order to mitigate "can't find node" errors
  Transforms.deselect(editor);

  // Remove existing nodes
  const children = [...editor.children];
  children.forEach((node) => editor.apply({ type: 'remove_node', path: [0], node }));

  // Add new nodes
  newEditorValue.forEach((node, i) => editor.apply({ type: 'insert_node', path: [i], node: node }));

  // Place cursor at the end of the new content
  const point = Editor.end(editor, []);
  Transforms.select(editor, point);
};

export const replaceSlateEditorText = (editor, newTextValue) => {
  if (!editor || newTextValue == null) {
    return;
  }

  const newValue = MarkedEditorUtils.deserialize({
    text: newTextValue,
  });
  replaceSlateEditorValue(editor, newValue);
};

export const useMergeForcedTextValue = (editor, forcedTextValue) => {
  useEffect(() => {
    if (forcedTextValue == null || !editor) {
      return;
    }

    replaceSlateEditorText(editor, forcedTextValue);
  }, [forcedTextValue, editor]);
};
